import React, { Component } from 'react';
import { createPortal } from 'react-dom';

import { Notification } from './Notification';
import { dequeue, QueuedNotification, subscribe } from './notificationQueue';

interface State {
  items: Array<QueuedNotification>;
}

/**
 * Renders every queued notification into #notifications, from inside the app's own React
 * tree.
 *
 * This replaces the old arrangement, where Notification.create called ReactDOM.render to
 * mount a fresh React root per notification. That works on React 17 but is deprecated in
 * 18 and removed in 19, and on 18 it silently drops the subtree into legacy mode. A
 * portal reaches the same DOM node while keeping one root.
 *
 * #notifications is a sibling of #root in public/index.handlebars, and a portal preserves
 * that DOM position exactly, so the existing stylesheet still applies. What changes is
 * that notifications are now inside the React tree for context and event bubbling —
 * strictly closer to how the rest of the app already works.
 */
export class NotificationHost extends Component<{}, State> {
  private unsubscribe?: () => void;

  public state: State = { items: [] };

  public componentDidMount() {
    this.unsubscribe = subscribe(items => this.setState({ items }));
  }

  public componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  public render() {
    const container = document.getElementById('notifications');

    if (!container) {
      return null;
    }

    return createPortal(
      this.state.items.map(({ id, props, resolve }) => (
        <Notification
          key={id}
          {...props}
          onResolved={resolve}
          onClosed={() => dequeue(id)}
        />
      )),
      container
    );
  }
}
